import { useEffect, useRef, useState } from 'react';

/** Em qual eixo a região corta o conteúdo. */
export type OverflowAxis = 'horizontal' | 'vertical';

/**
 * A região rolável que **responde ao teclado** — a medida, não o desenho.
 *
 * ## O defeito que isto conserta, e por que ele é invisível
 *
 * Uma região com `overflow` automático e **nenhum elemento focável dentro** não
 * alcança o teclado. Quem navega sem mouse não tem como ver o que está cortado
 * — é falha da WCAG 2.1.1, e não questão de estilo. Alguns navegadores já
 * fazem isso sozinhos; nem todos, e a régua não é o navegador de quem escreveu
 * a tela.
 *
 * Nada na tela denuncia: o conteúdo visível está todo no lugar certo.
 *
 * ## Por que o `tabIndex` é condicional
 *
 * Só entra na ordem de tabulação quando a região **de fato** transborda. Um
 * `tabIndex={0}` fixo poria uma parada de teclado em toda tabela da interna,
 * inclusive nas que cabem inteiras — parada que não leva a lugar nenhum é ruído
 * para quem depende do teclado, não acessibilidade.
 *
 * ## A medida é do navegador, e é refeita
 *
 * `scrollWidth > clientWidth` na horizontal, `scrollHeight > clientHeight` na
 * vertical, com `ResizeObserver` no quadro **e** nos filhos: o quadro muda com
 * a janela e o filho muda com o conteúdo — filtrar linhas muda a largura das
 * colunas, arquivar uma premiação muda a altura da pilha. Uma medida tirada só
 * na montagem ficaria errada no primeiro filtro.
 *
 * ## Por que ele saiu de dentro da `Table` (22/09/2026)
 *
 * Nasceu ali, medindo a horizontal, para a tabela que rola de lado no telefone.
 * A segunda região rolável da casa — a pilha de premiações do consultor, dentro
 * de um modal — precisa exatamente da mesma regra no outro eixo, e a alternativa
 * era copiar o hook. O que se copia é a linha do `scrollWidth`; o que não se
 * copia é o motivo dela existir, e é por isso que a segunda cópia nasceria sem
 * o `role`, sem o `aria-label` e sem o `tabIndex`.
 */
export function useOverflow(axis: OverflowAxis) {
  const ref = useRef<HTMLDivElement>(null);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const frame = ref.current;
    if (!frame) return;

    /*
     * O `+ 1` absorve o arredondamento de subpixel: num zoom de 110% o
     * `scrollWidth` volta 0,4px maior que o `clientWidth` numa tabela que cabe
     * inteira, e sem a folga a região entraria na tabulação sem ter o que rolar.
     */
    const measure = () =>
      setOverflows(
        axis === 'vertical'
          ? frame.scrollHeight > frame.clientHeight + 1
          : frame.scrollWidth > frame.clientWidth + 1,
      );
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    for (const child of frame.children) observer.observe(child);
    return () => observer.disconnect();
  });

  return { ref, overflows };
}
